import { ReactNode, useCallback } from "react";
import { useOnFilesChange, useStore } from "./state";

import Dropzone from "./Dropzone";
import Preview from "./Preview";
import { useSnapshot } from "@web-monorepo/hooks";
import { useAfterUnmount } from "@web-monorepo/hooks";
import type { FilePickerCallbacks } from "./types";

export default function FilePicker({
  allowedFileConfig,
  prompt,
  onCancel,
  onChange,
  onInvalidFileDrop,
  onInvalidFileName,
  onFileTooLarge,
  clearOnUnmount = false,
  allowMultipleDocumentsPreview = false,
  onMaxFilesReached,
  onPickerOpened,
  onPickerDismissed,
  onFilesRejected,
  ariaLabel,
}: {
  allowedFileConfig: {
    extensions: string[];
    maxFiles: number;
    maxFilenameLength?: number;
    maxFileSizeBytes?: number;
  };
  prompt: ReactNode;
  onChange?(files: File[]): void;
  clearOnUnmount?: boolean;
  allowMultipleDocumentsPreview?: boolean;
  ariaLabel?: string;
} & FilePickerCallbacks) {
  const store = useStore();
  const { files } = useSnapshot(store);

  const maxAllowedFiles = allowedFileConfig.maxFiles;

  useOnFilesChange(useCallback((files) => onChange?.(files), [onChange]));
  useAfterUnmount(() => {
    if (!clearOnUnmount) return;
    store.files = [];
  });

  return (
    <Dropzone
      allowedFileExtensions={allowedFileConfig.extensions}
      maxAllowedFiles={maxAllowedFiles}
      maxFilenameLength={allowedFileConfig.maxFilenameLength}
      maxFileSizeBytes={allowedFileConfig.maxFileSizeBytes}
      onCancel={onCancel}
      onInvalidFileDrop={onInvalidFileDrop}
      onInvalidFileName={onInvalidFileName}
      onFileTooLarge={onFileTooLarge}
      prompt={prompt}
      onMaxFilesReached={onMaxFilesReached}
      onPickerOpened={onPickerOpened}
      onPickerDismissed={onPickerDismissed}
      onFilesRejected={onFilesRejected}
      ariaLabel={ariaLabel}
    >
      <Preview files={files} allowMultipleDocumentsPreview={allowMultipleDocumentsPreview} />
    </Dropzone>
  );
}
